import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import EditRoundedIcon from '@mui/icons-material/EditRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { ErrorState, LoadingState } from '@/shared/ui';
import { formatQuantity } from '@/shared/lib/quantityFormat';

import {
  useChannelScopes,
  useDeleteChannelScope,
  useLocationGroups,
  useUpsertChannelScope,
} from '../../api/hooks';
import type { ChannelScope, LocationGroup } from '../../model/types';

/**
 * Canonical own/marketplace channel registry rendered as fixed rows (design spec §8.2 + Q1
 * fallback: the fixed canonical list; a registry endpoint can replace this constant later without
 * UI changes). Codes per adr-tenant-001.
 */
const CHANNEL_REGISTRY: { code: string; labelKey: string }[] = [
  { code: 'OWN_RETAIL', labelKey: 'channels.names.OWN_RETAIL' },
  { code: 'OWN_ONLINE', labelKey: 'channels.names.OWN_ONLINE' },
  { code: 'MP_OZON', labelKey: 'channels.names.MP_OZON' },
  { code: 'MP_WILDBERRIES', labelKey: 'channels.names.MP_WILDBERRIES' },
];

interface ChannelsPanelProps {
  canManage: boolean;
  onToast: (message: string, severity?: 'success' | 'error' | 'warning') => void;
}

/** "Channels" tab — ChannelStockScope binding (design-stock-channels-page §8.2). */
export function ChannelsPanel({ canManage, onToast }: ChannelsPanelProps) {
  const { t } = useTranslation('channelStock');
  const scopes = useChannelScopes();
  const groups = useLocationGroups();
  const upsert = useUpsertChannelScope();
  const remove = useDeleteChannelScope();

  const [dialogChannel, setDialogChannel] = useState<string | null>(null);
  const [confirmUnbind, setConfirmUnbind] = useState<string | null>(null);

  const scopeByChannel = useMemo(
    () => new Map((scopes.data ?? []).map((scope) => [scope.channelCode, scope])),
    [scopes.data],
  );
  const groupById = useMemo(
    () => new Map((groups.data ?? []).map((group) => [group.id, group])),
    [groups.data],
  );

  if (scopes.isLoading || groups.isLoading) return <LoadingState />;
  if (scopes.isError) return <ErrorState onRetry={() => void scopes.refetch()} />;

  const configured = new Set(CHANNEL_REGISTRY.map((entry) => entry.code));
  const extraScopes = (scopes.data ?? []).filter((scope) => !configured.has(scope.channelCode));

  const rows: { code: string; label: string; scope?: ChannelScope }[] = [
    ...CHANNEL_REGISTRY.map((entry) => ({
      code: entry.code,
      label: t(entry.labelKey, { defaultValue: entry.code }),
      scope: scopeByChannel.get(entry.code),
    })),
    ...extraScopes.map((scope) => ({ code: scope.channelCode, label: scope.channelCode, scope })),
  ];

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
          {t('channels.hint')}
        </Typography>
        <Tooltip title={t('refresh')}>
          <IconButton onClick={() => void scopes.refetch()} aria-label="refresh-channels">
            <RefreshRoundedIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      {scopes.isFetching && !scopes.isLoading ? <LinearProgress sx={{ height: 4 }} /> : <span style={{ height: 4, display: 'block' }} />}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('channels.columns.channel')}</TableCell>
              <TableCell>{t('channels.columns.group')}</TableCell>
              <TableCell>{t('channels.columns.minQty')}</TableCell>
              <TableCell>{t('channels.columns.status')}</TableCell>
              <TableCell align="right">{t('columns.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.code} hover>
                <TableCell>
                  <Typography sx={{ fontWeight: 600 }}>{row.label}</Typography>
                  <Typography variant="caption" sx={{ fontFamily: '"JetBrains Mono", monospace' }} color="text.secondary">
                    {row.code}
                  </Typography>
                </TableCell>
                <TableCell>{row.scope ? groupById.get(row.scope.groupId)?.name ?? row.scope.groupId : '—'}</TableCell>
                <TableCell>{row.scope?.minAvailableQty != null ? formatQuantity(row.scope.minAvailableQty) : '—'}</TableCell>
                <TableCell>
                  {row.scope ? (
                    <Chip size="small" color="success" variant="outlined" label={t('channels.status.bound')} />
                  ) : (
                    <Stack spacing={0}>
                      <Chip size="small" variant="outlined" label={t('channels.status.unbound')} sx={{ width: 'fit-content' }} />
                      <Typography variant="caption" color="text.secondary">{t('channels.legacyHint')}</Typography>
                    </Stack>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={canManage ? t('channels.configure') : t('noPermissionTooltip')}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={!canManage}
                        aria-label={`configure-channel-${row.code}`}
                        onClick={() => setDialogChannel(row.code)}
                      >
                        <EditRoundedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {row.scope ? (
                    <Tooltip title={canManage ? t('channels.unbind') : t('noPermissionTooltip')}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={!canManage}
                          aria-label={`unbind-channel-${row.code}`}
                          onClick={() => setConfirmUnbind(row.code)}
                        >
                          <LinkOffRoundedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {dialogChannel !== null ? (
        <ChannelDialog
          channelCode={dialogChannel}
          scope={scopeByChannel.get(dialogChannel)}
          groups={groups.data ?? []}
          pending={upsert.isPending}
          onClose={() => setDialogChannel(null)}
          onSave={(body) =>
            upsert.mutate(
              { channelCode: dialogChannel, body },
              {
                onSuccess: () => {
                  setDialogChannel(null);
                  onToast(t('channels.saved'));
                },
                onError: (error) => onToast(error.message, 'error'),
              },
            )
          }
        />
      ) : null}

      <Dialog
        open={confirmUnbind !== null}
        onClose={remove.isPending ? undefined : () => setConfirmUnbind(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t('channels.unbindConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('channels.unbindConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUnbind(null)}>{t('cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            disabled={remove.isPending}
            startIcon={remove.isPending ? <CircularProgress size={16} /> : undefined}
            onClick={() => {
              if (!confirmUnbind) return;
              remove.mutate(confirmUnbind, {
                onSuccess: () => {
                  setConfirmUnbind(null);
                  onToast(t('channels.unbound'));
                },
                onError: (error) => onToast(error.message, 'error'),
              });
            }}
          >
            {t('channels.unbindAction')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

interface ChannelDialogProps {
  channelCode: string;
  scope?: ChannelScope;
  groups: LocationGroup[];
  pending: boolean;
  onClose: () => void;
  onSave: (body: { groupId: string; minAvailableQty: number | null }) => void;
}

function ChannelDialog({ channelCode, scope, groups, pending, onClose, onSave }: ChannelDialogProps) {
  const { t } = useTranslation('channelStock');
  const [groupId, setGroupId] = useState(scope?.groupId ?? '');
  const [minQty, setMinQty] = useState(scope?.minAvailableQty != null ? String(scope.minAvailableQty) : '');

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={pending ? undefined : onClose}>
      <DialogTitle>{t('channels.dialogTitle', { channel: channelCode })}</DialogTitle>
      <DialogContent>
        {groups.length === 0 ? (
          <Typography sx={{ mt: 1 }}>{t('channels.noGroupsHint')}</Typography>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              required
              label={t('channels.groupLabel')}
              value={groupId}
              onChange={(event) => setGroupId(event.target.value)}
              helperText={t('channels.groupHelper')}
            >
              {groups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name} ({t('groups.locationsCount', { count: group.locationIds.length })})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('channels.minQtyLabel')}
              helperText={t('channels.minQtyHelper')}
              value={minQty}
              onChange={(event) => setMinQty(event.target.value)}
              type="number"
              slotProps={{ htmlInput: { min: 0, step: 'any' } }}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>{t('cancel')}</Button>
        <Button
          variant="contained"
          disabled={!groupId || pending}
          startIcon={pending ? <CircularProgress size={16} /> : undefined}
          onClick={() => onSave({ groupId, minAvailableQty: minQty.trim() === '' ? null : Number(minQty) })}
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
